import React from 'react';
import { Link } from 'react-router-dom';
import { Mic, BrainCircuit, Download as DownloadIcon, ArrowRight, ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import useVersions from '../lib/useVersions';
import { Button } from '../components/ui/button';

const APPS = [
  {
    key: 'recorder',
    icon: Mic,
    name: 'Call Recorder',
    tagline: 'Captures your calls',
    body: 'Runs in the background and saves each call as clean audio for the processor to analyze.',
    fallbackUrl: '/downloads/CallRecorder-Installer.zip',
  },
  {
    key: 'processor',
    icon: BrainCircuit,
    name: 'Call Processor',
    tagline: 'Transcribes and writes the follow-up',
    body: 'Transcribes locally, drafts the follow-up packet, and syncs your sheet and calendar. Downloads the transcription model on first run.',
    fallbackUrl: '/downloads/CallProcessor-Installer.zip',
  },
];

function AppCard({ app, manifest, loading }) {
  const url = manifest?.url || app.fallbackUrl;
  const version = manifest?.version;
  return (
    <div className="flex flex-col rounded-2xl border border-border bg-card p-7 shadow-card">
      <div className="flex items-start justify-between">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground">
          <app.icon size={22} />
        </span>
        <span className="rounded-full border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground">
          {loading ? 'Checking…' : version ? `Latest v${version}` : 'Windows'}
        </span>
      </div>
      <h3 className="mt-5 font-display text-xl font-semibold text-foreground">{app.name}</h3>
      <p className="text-sm font-medium text-accent">{app.tagline}</p>
      <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{app.body}</p>
      {manifest?.notes && (
        <p className="mt-3 rounded-lg bg-muted px-3 py-2 text-xs text-muted-foreground">
          Release notes: {manifest.notes}
        </p>
      )}
      <a href={url} download className="mt-6">
        <Button className="w-full" size="lg">
          <DownloadIcon size={18} />
          Download for Windows
        </Button>
      </a>
    </div>
  );
}

export default function Download() {
  const { user } = useAuth();
  const { data, loading } = useVersions();

  return (
    <div className="container-page py-16">
      <div className="max-w-2xl">
        <h1 className="font-display text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
          Download the apps
        </h1>
        <p className="mt-3 text-lg text-muted-foreground">
          You are signed in as <span className="font-medium text-foreground">{user?.email}</span>.
          Install both apps on your Windows machine, then follow the setup guide.
        </p>
      </div>

      <div className="mt-10 grid gap-6 md:grid-cols-2">
        {APPS.map((app) => (
          <AppCard key={app.key} app={app} manifest={data?.[app.key]} loading={loading} />
        ))}
      </div>

      <div className="mt-8 flex flex-col items-start gap-4 rounded-2xl border border-border bg-card/50 p-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <ShieldCheck size={20} className="mt-0.5 shrink-0 text-accent" />
          <div>
            <p className="text-sm font-medium text-foreground">First time installing?</p>
            <p className="text-sm text-muted-foreground">
              The setup guide covers the Windows App Control prompt, your <code>.env</code> keys, and
              the Google Sheet hookup.
            </p>
          </div>
        </div>
        <Link to="/setup" className="shrink-0">
          <Button variant="outline">
            Open setup guide <ArrowRight size={16} />
          </Button>
        </Link>
      </div>

      <p className="mt-6 text-sm text-muted-foreground">
        Both apps check for updates on launch and update themselves — you will not need to come back
        here for new versions.
      </p>
    </div>
  );
}
